import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { dataModelsService } from '../api/dataModelsService';
import type { DataModelListItem, DataModelDetail, DataModelStatus } from '../api/dataModelsService';
import { useAuth } from './AuthContext';

interface DataModelsContextType {
  dataModels: DataModelListItem[];
  currentDataModel: DataModelDetail | null;
  isLoading: boolean;
  error: string | null;
  loadDataModels: () => Promise<void>;
  loadDataModel: (id: string) => Promise<DataModelDetail>;
  createDataModel: (name: string, connectionId: string, description?: string) => Promise<DataModelDetail>;
  updateDataModel: (id: string, data: { name?: string; description?: string; status?: DataModelStatus }) => Promise<void>;
  deleteDataModel: (id: string) => Promise<void>;
  setCurrentDataModel: (model: DataModelDetail | null) => void;
  refreshCurrentDataModel: () => Promise<void>;
  /** Active build job IDs keyed by data model ID (survives navigation) */
  buildJobIds: Record<string, string>;
  setBuildJobId: (dataModelId: string, jobId: string | null) => void;
  /** Optimistically update a data model's status in the list */
  updateDataModelStatusLocally: (dataModelId: string, status: DataModelStatus) => void;
}

const DataModelsContext = createContext<DataModelsContextType | undefined>(undefined);

export const DataModelsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [dataModels, setDataModels] = useState<DataModelListItem[]>([]);
  const [currentDataModel, setCurrentDataModel] = useState<DataModelDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buildJobIds, setBuildJobIds] = useState<Record<string, string>>({});

  // Ref to access currentDataModel inside stable callbacks without adding it as a dependency
  const currentDataModelRef = useRef(currentDataModel);
  currentDataModelRef.current = currentDataModel;

  const updateDataModelStatusLocally = useCallback((dataModelId: string, status: DataModelStatus) => {
    setDataModels(prev =>
      prev.map(m => m.id === dataModelId ? { ...m, status } : m)
    );
  }, []);

  const setBuildJobId = useCallback((dataModelId: string, jobId: string | null) => {
    setBuildJobIds(prev => {
      if (jobId === null) {
        const { [dataModelId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [dataModelId]: jobId };
    });
  }, []);

  const loadDataModels = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const models = await dataModelsService.list();
      setDataModels(models);

      // If a data model is currently loaded and its status changed (e.g. building → active),
      // re-fetch the full detail so views/discovery data are available.
      const cur = currentDataModelRef.current;
      if (cur) {
        const updated = models.find(m => m.id === cur.id);
        if (updated && updated.status !== cur.status) {
          const detail = await dataModelsService.getById(cur.id);
          setCurrentDataModel(detail);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data models');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadDataModel = useCallback(async (id: string): Promise<DataModelDetail> => {
    const model = await dataModelsService.getById(id);
    setCurrentDataModel(model);
    return model;
  }, []);

  const createDataModel = useCallback(async (
    name: string,
    connectionId: string,
    description?: string
  ): Promise<DataModelDetail> => {
    const model = await dataModelsService.create({ name, connectionId, description });
    // Refresh the list
    await loadDataModels();
    setCurrentDataModel(model);
    return model;
  }, [loadDataModels]);

  const updateDataModel = useCallback(async (
    id: string,
    data: { name?: string; description?: string; status?: DataModelStatus }
  ) => {
    const updated = await dataModelsService.update(id, data);
    setCurrentDataModel(updated);
    await loadDataModels();
  }, [loadDataModels]);

  const deleteDataModel = useCallback(async (id: string) => {
    // Optimistically remove from local state to avoid flicker
    setDataModels(prev => prev.filter(m => m.id !== id));
    if (currentDataModel?.id === id) {
      setCurrentDataModel(null);
    }
    try {
      await dataModelsService.delete(id);
    } catch {
      // Rollback: reload the full list on failure
      await loadDataModels();
    }
  }, [currentDataModel, loadDataModels]);

  const refreshCurrentDataModel = useCallback(async () => {
    if (currentDataModel?.id) {
      const model = await dataModelsService.getById(currentDataModel.id);
      setCurrentDataModel(model);
    }
  }, [currentDataModel?.id]);

  // Load data models when authenticated (and re-fetch on login)
  useEffect(() => {
    if (isAuthenticated) {
      loadDataModels();
    } else {
      setDataModels([]);
    }
  }, [isAuthenticated, loadDataModels]);

  return (
    <DataModelsContext.Provider
      value={{
        dataModels,
        currentDataModel,
        isLoading,
        error,
        loadDataModels,
        loadDataModel,
        createDataModel,
        updateDataModel,
        deleteDataModel,
        setCurrentDataModel,
        refreshCurrentDataModel,
        buildJobIds,
        setBuildJobId,
        updateDataModelStatusLocally,
      }}
    >
      {children}
    </DataModelsContext.Provider>
  );
};

export const useDataModels = (): DataModelsContextType => {
  const context = useContext(DataModelsContext);
  if (!context) {
    throw new Error('useDataModels must be used within a DataModelsProvider');
  }
  return context;
};